import React, { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import { isValidEmail } from "common/utils/strings";
import { buildGuestCustomObjectURL } from "common/utils/url";
import {
  InformationRequestContactType,
  useShareCustomMapGeometryMutation,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import {
  ButtonSection,
  Container,
  ContentSection,
  FormSection,
  HeaderSection,
  InputRow,
  PrimaryButtons,
  SecondaryButtons,
} from "../Common/__styles__/Modal";
import { ButtonLink } from "../Common/ButtonLink";
import { Button } from "../Common/Buttons/Button";
import Modal from "../Common/Modal";
import { CONTACT_TYPE_OPTIONS } from "../Common/ShareItem/utils";
import { Body } from "../Common/Typography";
import { Text as NonHookText } from "../Inputs";
import { Checkbox, Select, Text } from "../Inputs/react-hook-form";

interface ShareCustomObjectModalProps {
  customMapId: string;
  geometryId: string;
  objectName: string;
  objectType: string;
}

export const useShareCustomObjectModal = ({
  customMapId,
  geometryId,
  objectName,
  objectType,
}: ShareCustomObjectModalProps) => {
  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={hideModal}>
        <ShareCustomObjectForm
          onClose={hideModal}
          customMapId={customMapId}
          geometryId={geometryId}
          objectName={objectName}
          objectType={objectType}
        />
      </Modal>
    ),
    [customMapId, geometryId, objectName, objectType],
  );

  return [showModal, hideModal] as const;
};

type ShareCustomObjectFormStructure = {
  geometryId: string;
  recipientFirstName: string;
  recipientLastName: string;
  recipientEmail: string;
  recipientType: InformationRequestContactType;
  copyYourself: boolean;
};

type ShareCustomObjectFormProps = ShareCustomObjectModalProps & {
  onClose: () => void;
};

const ShareCustomObjectForm = ({
  onClose,
  customMapId,
  geometryId,
  objectName,
  objectType,
}: ShareCustomObjectFormProps) => {
  const { account, user, admin } = useContext(AuthContext);
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const [showSuccess, setShowSuccess] = useState(false);

  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    register,
  } = useForm<ShareCustomObjectFormStructure>({
    defaultValues: {
      geometryId,
      recipientType: InformationRequestContactType.PROPERTY_OWNER,
      copyYourself: true,
    },
  });

  const [shareCustomMapGeometry, { loading }] =
    useShareCustomMapGeometryMutation({
      onCompleted: () => {
        track("Shared Custom Object", {
          geometryLabel: objectName,
          customMapName: objectType,
        });
        setShowSuccess(true);
      },
      onError: () => {
        addErrorToast(
          `There was an error sharing this object. Please try again. If the problem persists, please email us at support@withforerunner.com`,
        );
      },
    });

  const onSubmit = async (data: ShareCustomObjectFormStructure) => {
    await shareCustomMapGeometry({ variables: { data } });
  };

  if (showSuccess) {
    return (
      <Container>
        <HeaderSection>
          <h1>Share {objectType.toLowerCase()} info</h1>
        </HeaderSection>
        <ContentSection>
          <p>
            {`A public link has been successfully sent to the following email: ${getValues("recipientEmail")}.`}
          </p>
        </ContentSection>
        <ButtonSection>
          <PrimaryButtons>
            <Button
              onClick={onClose}
              size="medium"
              variant="secondary"
              type="button"
              title="Close"
            />
          </PrimaryButtons>
        </ButtonSection>
      </Container>
    );
  }

  const objectURL = buildGuestCustomObjectURL({
    subdomain: account!.subdomain,
    customMapId,
    geometryId,
  });

  return (
    <Container overflows>
      <HeaderSection>
        <h1>Share {objectType.toLowerCase()} info</h1>
        <h2>Send a public object profile link to a recipient</h2>
      </HeaderSection>
      <FormSection overflows>
        <ContentSection overflows>
          <Body size="default" type="regular">
            The recipient will receive an email with a link to a public profile
            of the object. This public link will not allow the recipient to edit
            or delete any information.
          </Body>
          <InputRow>
            <NonHookText
              name="object"
              label={objectType}
              value={objectName}
              disabled={true}
              size="small"
            />
          </InputRow>
          <InputRow>
            <Text
              {...register("recipientFirstName", {
                required: "Recipient's first name is required",
              })}
              error={errors.recipientFirstName?.message}
              size="small"
              label="Recipient first name"
              required
            />
            <Text
              {...register("recipientLastName", {
                required: "Recipient's last name is required",
              })}
              error={errors.recipientLastName?.message}
              size="small"
              label="Recipient last name"
              required
            />
          </InputRow>
          <InputRow style={{ lineHeight: "1" }}>
            <Text
              {...register("recipientEmail", {
                validate: val => {
                  if (!val) {
                    return "Required field";
                  }
                  if (!isValidEmail(val)) {
                    return "Email is invalid";
                  }
                  return;
                },
              })}
              error={errors.recipientEmail?.message}
              size="small"
              label="Recipient email"
              required
            />
            <Select
              control={control}
              name="recipientType"
              label="Recipient type"
              required
              options={CONTACT_TYPE_OPTIONS}
            />
          </InputRow>
          <Checkbox
            control={control}
            name="copyYourself"
            label={`Copy yourself on email (${user?.email ?? admin?.email})`}
          />
        </ContentSection>
        <ButtonSection>
          <SecondaryButtons>
            <ButtonLink
              onClick={() => {
                track("Clicked view custom object button", {
                  geometryLabel: objectName,
                  customMapName: objectType,
                });

                window.open(objectURL, "_blank");
              }}
              size="medium"
              styleVariant="primary"
              leftIconName="eye"
            >
              View public profile
            </ButtonLink>
            <ButtonLink
              onClick={async () => {
                track("Copied Custom Object Link", {
                  geometryLabel: objectName,
                  customMapName: objectType,
                });

                await navigator.clipboard.writeText(objectURL);
                addSuccessToast("Link copied to clipboard");
              }}
              size="medium"
              styleVariant="primary"
              leftIconName="link"
            >
              Copy url link
            </ButtonLink>
          </SecondaryButtons>
          <PrimaryButtons>
            <Button
              onClick={handleSubmit(onSubmit)}
              isPending={loading}
              size="medium"
              variant="primary"
              type="button"
              title="Send"
            />
          </PrimaryButtons>
        </ButtonSection>
      </FormSection>
    </Container>
  );
};

export default ShareCustomObjectForm;
